import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2.5 whitespace-nowrap text-sm font-semibold ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 uppercase tracking-wide hover:scale-105 active:scale-95",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_4px_12px_hsl(220_15%_20%/0.25)] hover:shadow-[0_6px_16px_hsl(220_15%_20%/0.35)]",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-[0_4px_12px_hsl(220_15%_20%/0.25)] hover:shadow-[0_6px_16px_hsl(220_15%_20%/0.35)]",
        outline: "border-2 border-primary bg-transparent text-primary hover:bg-primary hover:text-primary-foreground shadow-[0_4px_12px_hsl(220_15%_20%/0.15)] hover:shadow-[0_6px_16px_hsl(220_15%_20%/0.25)]",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 shadow-[0_4px_12px_hsl(220_15%_20%/0.15)] hover:shadow-[0_6px_16px_hsl(220_15%_20%/0.25)]",
        ghost: "hover:bg-secondary hover:text-foreground",
        link: "text-accent underline-offset-4 hover:underline lowercase tracking-normal",
        // Primary CTA - Dark grey shadow
        cta: "bg-accent text-accent-foreground hover:brightness-110 shadow-[0_4px_16px_hsl(220_15%_20%/0.3)] hover:shadow-[0_6px_20px_hsl(220_15%_20%/0.4)]",
        // WhatsApp CTA - Dark grey shadow
        whatsapp: "bg-whatsapp text-whatsapp-foreground hover:brightness-110 shadow-[0_4px_16px_hsl(220_15%_20%/0.3)] hover:shadow-[0_6px_20px_hsl(220_15%_20%/0.4)]",
        // Outline on dark backgrounds
        outlineLight: "border-2 border-accent/60 bg-transparent text-primary-foreground hover:bg-accent/10 hover:border-accent shadow-[0_4px_12px_hsl(220_15%_20%/0.2)] hover:shadow-[0_6px_16px_hsl(220_15%_20%/0.3)]",
      },
      size: {
        default: "h-11 px-6 py-2 rounded-lg",
        sm: "h-9 rounded-lg px-4 text-xs",
        lg: "h-12 rounded-lg px-8 text-sm",
        xl: "h-14 rounded-lg px-10 text-sm",
        icon: "h-11 w-11 rounded-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
